import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Button,
    Box,
    CircularProgress,
    Alert,
    Chip,
    Paper,
    Pagination,
    Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axios';

const MyExams = () => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchSubscriptions = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`/api/subscriptions/user?page=${page}&limit=20`);
                setSubscriptions(response.data?.subscriptions || []);
                setTotalPages(response.data?.pages || 1);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching subscriptions:", err);
                setError(err.response?.data?.message || 'Failed to fetch subscriptions');
                setLoading(false);
            }
        };

        fetchSubscriptions();
    }, [page]);

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const handleStartExam = (examId) => {
        navigate(`/exams/${examId}/take`);
    };

    const handleViewExam = (examId) => {
        navigate(`/exams/${examId}`);
    };

    // Check if a subscription is active (not expired)
    const isSubscriptionActive = (endDate) => {
        return new Date(endDate) > new Date();
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                           'July', 'August', 'September', 'October', 'November', 'December'];
        const month = monthNames[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear();
        return `${month} ${day}, ${year}`;
    };

    if (loading && page === 1) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container>
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <Container sx={{ py: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                My Exams
            </Typography>

            {(!subscriptions || subscriptions.length === 0) ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        You haven't subscribed to any exams yet. Browse our available exams to get started.
                    </Alert>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        size="large"
                        onClick={() => navigate('/exams')}
                    >
                        Browse Exams
                    </Button>
                </Paper>
            ) : (
                <>
                    <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                        {subscriptions.map((subscription) => (
                            <Paper key={subscription._id} sx={{ mb: 2 }}>
                                <ListItem>
                                    <Box sx={{ width: '100%', pr: 10 }}>
                                        <Typography variant="h6">
                                            {subscription.exam?.title || 'Unknown Exam'}
                                        </Typography>
                                        <Box sx={{ mt: 1 }}>
                                            <Typography 
                                                variant="body2" 
                                                color="text.secondary" 
                                                paragraph 
                                                component="div"
                                            >
                                                {subscription.exam?.description?.substring(0, 200) || 'No description'}
                                                {subscription.exam?.description?.length > 200 ? '...' : ''}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <Chip 
                                                    label={isSubscriptionActive(subscription.endDate) ? 'Active' : 'Expired'} 
                                                    color={isSubscriptionActive(subscription.endDate) ? 'success' : 'error'} 
                                                    size="small" 
                                                />
                                                <Chip 
                                                    label={`Valid until: ${formatDate(subscription.endDate)}`}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                    <ListItemSecondaryAction>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            {isSubscriptionActive(subscription.endDate) ? (
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => handleStartExam(subscription.exam?._id)}
                                                >
                                                    Start Exam
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => handleViewExam(subscription.exam?._id)}
                                                >
                                                    Renew Subscription
                                                </Button>
                                            )}
                                            <Button
                                                variant="outlined"
                                                onClick={() => handleViewExam(subscription.exam?._id)}
                                            >
                                                View Details
                                            </Button>
                                        </Box>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            </Paper>
                        ))}
                    </List>

                    {totalPages > 1 && (
                        <Box display="flex" justifyContent="center" mt={4}>
                            <Pagination 
                                count={totalPages} 
                                page={page} 
                                onChange={handlePageChange} 
                                color="primary" 
                                size="large"
                                disabled={loading}
                            />
                        </Box>
                    )}

                    <Box sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
                        <Button 
                            variant="contained" 
                            color="primary"
                            size="large" 
                            onClick={() => navigate('/exams')}
                        >
                            Browse More Exams
                        </Button>
                    </Box>
                </>
            )}
        </Container>
    );
};

export default MyExams; 